import { useEffect, useRef, useState } from "react";
import { Check, Mic, Sparkles, Square, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button, Textarea } from "./kd";

export type KiCtx = { fall_id?: string; token?: string; slug?: string; patient?: Record<string, string> };
export type KiFlags = { sprache: boolean; assistent: boolean };
const MAX_SEC = 180;

export function useKi(ctx: KiCtx | null): KiFlags {
  const [f, setF] = useState<KiFlags>({ sprache: false, assistent: false });
  const key = ctx ? `${ctx.fall_id || ""}|${ctx.token || ""}|${ctx.slug || ""}` : "";
  useEffect(() => {
    if (!ctx) return;
    api.post("/ki/status", { fall_id: ctx.fall_id, token: ctx.token, slug: ctx.slug }).then((r) => setF(r.data)).catch(() => undefined);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return f;
}

const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => (window as any).MediaRecorder?.isTypeSupported?.(m)) || "";

export function MicButton({ ctx, onText, testid = "mic-button" }: { ctx: KiCtx; onText: (t: string) => void; testid?: string }) {
  const [state, setState] = useState<"idle" | "rec" | "busy">("idle");
  const [sec, setSec] = useState(0);
  const rec = useRef<MediaRecorder | null>(null);
  const timer = useRef<any>(null);
  const peak = useRef({ max: 0, clip: 0, n: 0 });
  const stop = () => { if (rec.current?.state === "recording") rec.current.stop(); };
  useEffect(() => () => { clearInterval(timer.current); stop(); }, []);
  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !(window as any).MediaRecorder) { toast.error("Ihr Browser unterstützt keine Audioaufnahme."); return; }
    let stream: MediaStream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch { toast.error("Kein Zugriff auf das Mikrofon. Bitte den Zugriff in den Browser-Einstellungen erlauben."); return; }
    const audioCtx = new AudioContext();
    const an = audioCtx.createAnalyser(); audioCtx.createMediaStreamSource(stream).connect(an);
    const buf = new Float32Array(an.fftSize);
    peak.current = { max: 0, clip: 0, n: 0 };
    const mime = pickMime();
    const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks: Blob[] = [];
    const t0 = Date.now();
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = async () => {
      clearInterval(timer.current); stream.getTracks().forEach((t) => t.stop()); audioCtx.close();
      const dauer = (Date.now() - t0) / 1000;
      const p = peak.current;
      if (p.max < 0.03) { setState("idle"); toast.error("Die Aufnahme war zu leise. Bitte näher am Mikrofon sprechen."); return; }
      if (p.n && p.clip / p.n > 0.2) toast.warning("Die Aufnahme war sehr laut – bitte etwas mehr Abstand zum Mikrofon halten.");
      setState("busy");
      const fd = new FormData();
      fd.append("audio", new Blob(chunks, { type: (mime || "audio/webm").split(";")[0] }), mime.includes("mp4") ? "aufnahme.mp4" : "aufnahme.webm");
      fd.append("dauer", String(dauer));
      Object.entries(ctx).forEach(([k, v]) => { if (typeof v === "string" && v) fd.append(k, v); });
      try { const res = await api.post("/ki/transkribieren", fd, { timeout: 90000 }); onText(res.data.text); }
      catch (e: any) { toast.error(e?.code === "ECONNABORTED" ? "Zeitüberschreitung bei der Spracherkennung. Bitte erneut versuchen." : errMsg(e)); }
      finally { setState("idle"); }
    };
    rec.current = r; r.start(1000); setState("rec"); setSec(0);
    timer.current = setInterval(() => {
      an.getFloatTimeDomainData(buf);
      const m = buf.reduce((a, v) => Math.max(a, Math.abs(v)), 0);
      peak.current.max = Math.max(peak.current.max, m); peak.current.n++; if (m > 0.98) peak.current.clip++;
      const s = Math.floor((Date.now() - t0) / 1000); setSec(s);
      if (s >= MAX_SEC) { toast.info("Maximale Aufnahmedauer von 3 Minuten erreicht."); stop(); }
    }, 250);
  };
  const mmss = `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
  return (
    <span className="inline-flex items-center gap-2">
      <button type="button" onClick={state === "rec" ? stop : start} disabled={state === "busy"} data-testid={testid}
        aria-label={state === "rec" ? "Aufnahme beenden" : "Spracheingabe starten"}
        className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors disabled:opacity-50 ${state === "rec" ? "border-danger bg-danger text-white" : "border-border bg-surface text-primary hover:bg-primary-light"}`}>
        {state === "rec" && <span className="absolute inset-0 animate-ping rounded-full bg-danger/40" aria-hidden />}
        {state === "rec" ? <Square className="relative h-4 w-4" aria-hidden /> : <Mic className="h-5 w-5" aria-hidden />}
      </button>
      {state === "rec" && <><span className="text-sm font-medium text-danger" data-testid={`${testid}-timer`}>{mmss}</span><Button variant="ghost" onClick={stop} data-testid={`${testid}-done`}>Fertig</Button></>}
      {state === "busy" && <span className="text-sm text-text-muted">Wird umgewandelt …</span>}
    </span>
  );
}

export const KI_HINT = "KI-Vorschlag – bitte vor dem Senden prüfen.";

export function KiPreview({ text, ctx, aktion, onAccept, onClose, acceptLabel = "Übernehmen" }:
  { text: string; ctx: KiCtx; aktion: string; onAccept: (t: string) => void; onClose: () => void; acceptLabel?: string }) {
  const [v, setV] = useState(text);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setV(text), [text]);
  const decide = (entscheidung: string) => api.post("/ki/entscheidung", { ...ctx, entscheidung, aktion }).catch(() => undefined);
  return (
    <div className="mt-3 rounded-lg border border-primary bg-primary-light/40 p-3" data-testid="ki-preview">
      <p className="mb-2 flex items-center gap-1 text-sm font-semibold text-primary"><Sparkles className="h-4 w-4" aria-hidden />KI-Vorschlag</p>
      <Textarea ref={ref} aria-label="KI-Vorschlag" rows={5} value={v} onChange={(e) => setV(e.target.value)} data-testid="ki-preview-text" />
      <p className="mt-1 text-xs text-text-muted" data-testid="ki-hint">{KI_HINT}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button onClick={() => { decide("uebernommen"); onAccept(v); onClose(); }} data-testid="ki-accept"><Check className="h-4 w-4" aria-hidden />{acceptLabel}</Button>
        <Button variant="secondary" onClick={() => ref.current?.focus()} data-testid="ki-edit">Bearbeiten</Button>
        <Button variant="ghost" onClick={() => { decide("verworfen"); onClose(); }} data-testid="ki-discard"><X className="h-4 w-4" aria-hidden />Verwerfen</Button>
      </div>
    </div>
  );
}

export function useKiText(ctx: KiCtx) {
  const [busy, setBusy] = useState("");
  const [s, setS] = useState<{ aktion: string; text: string } | null>(null);
  const ask = async (aktion: string, text: string) => {
    setBusy(aktion);
    try { const r = await api.post("/ki/text", { ...ctx, aktion, text }, { timeout: 90000 }); setS({ aktion, text: r.data.vorschlag }); return r.data; }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return { busy, suggestion: s, clear: () => setS(null), ask };
}

export const CHAT_AKTIONEN: Record<string, string> = { hoeflich: "Höflich und kollegial formulieren", kuerzer: "Kürzer fassen", rueckfrage: "Rückfrage formulieren", antwort: "Antwortvorschlag" };

export function KiMenu({ aktionen, busy, onPick }: { aktionen: Record<string, string>; busy: string; onPick: (a: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <Button variant="secondary" onClick={() => setOpen(!open)} loading={!!busy} aria-expanded={open} data-testid="ki-help-button"><Sparkles className="h-4 w-4" aria-hidden />KI-Hilfe</Button>
      {open && (
        <ul className="absolute bottom-full right-0 z-30 mb-1 w-64 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-surface p-1 shadow-lg sm:left-0 sm:right-auto" role="menu">
          {Object.entries(aktionen).map(([k, l]) => (
            <li key={k}><button role="menuitem" className="min-h-[44px] w-full rounded-md px-3 text-left hover:bg-bg" onClick={() => { setOpen(false); onPick(k); }} data-testid={`ki-action-${k}`}>{l}</button></li>))}
        </ul>)}
    </div>
  );
}
